// NewRoomDialog — "+ New Room" flow on BuildingCaptureScreen (PRD §7.2.3,
// buildspec 20-ui §5.6). Two steps inside the same modal:
//
//   1. Floor — segmented list of contract-scoped floor options
//      (admin-curated globally; see PR #421 / migration 0008). The
//      `floorsLoading` prop differentiates "still syncing" from
//      "actually empty" so a freshly-launched device doesn't see
//      the empty-state copy while reference_global is still in flight.
//   2. Room type — scrollable list of serialisable (non-pseudo) room
//      types. Pseudo room types never appear because the picker filters
//      on `isSerialisable === true`; the backend's matching 400
//      "pseudo_room_type" error is surfaced defensively if a stale
//      server catalogue lets one slip through.
//
// On confirm the dialog dispatches `createSpace({sessionId, floorId,
// roomTypeId})` via the capture client (buildspec 20-ui §6 / C2.7).
// The authoritative `SpaceWithItems` returned by the server is handed
// to `onCreated`, which is the caller's seam to append the new space
// to the session store and navigate to `/capture/spaces/:id` (buildspec
// 20-ui §5.6, §6.5: "Create room: NOT optimistic" — we wait for the
// server-assigned id + serialNumber before any UI commitment).
//
// a11y + focus: the underlying `<Dialog>` primitive provides the focus
// trap and Esc-closes behaviour. `onClose` is ignored while a create
// is in flight so the user can't cancel a half-acknowledged mutation.
//
// Data inputs (`floors`, `roomTypes`) are intentionally prop-injected
// — BuildingCaptureScreen already holds the building-detail response
// and a shared room-type list, so the dialog stays fetch-free and
// single-purpose.

import { useCallback, useMemo, useState, type ReactElement } from 'react'

import type {
  CaptureItemRow,
  SpaceWithItems,
} from '@citycare/shared'

import { ApiError } from '../../hooks/api'
import { Alert } from '../../ui/alert'
import { Button } from '../../ui/button'
import { Dialog } from '../../ui/dialog'
import { FormField } from '../../ui/form-field'
import { Spinner } from '../../ui/spinner'
import { TextInput } from '../../ui/text-input'
import type { CaptureClient } from '../client/capture-client.types'

import { resolveFloor } from './resolve-floor'

// Max length of the optional surveyor-supplied room name. Matches the
// spaces.alias varchar(255) column and the capture service's 255-char
// cap, so the input can't accept a value the backend would reject.
const ALIAS_MAX_LENGTH = 255

export interface NewRoomDialogFloor {
  readonly id: string
  readonly name: string
}

export interface NewRoomDialogRoomType {
  readonly id: string
  readonly name: string
  readonly isSerialisable: boolean
  readonly displayOrder: number
}

export interface NewRoomDialogProps {
  open: boolean
  onClose: () => void
  sessionId: string
  floors: readonly NewRoomDialogFloor[]
  roomTypes: readonly NewRoomDialogRoomType[]
  // True while the contract-scoped floor_options are still loading
  // (initial reference_global sync hasn't delivered the rows yet).
  // Defaults to false so callers that hand a populated `floors` list
  // synchronously don't have to thread a flag.
  floorsLoading?: boolean
  // Mutation seam — the capture client's `dispatch` from C2.7. Kept
  // narrow (not the full `CaptureClient`) so the test harness can
  // supply a tiny stub without needing a queue + event bus.
  dispatch: CaptureClient['dispatch']
  // Authoritative SpaceWithItems from the server. The parent appends
  // it to the session store and triggers navigation to the new space.
  onCreated: (space: SpaceWithItems) => void
}

type Step = 'floor' | 'roomType'

// `min-w-0` neutralises the default `min-width: auto` on flex items so a
// long unbreakable label (e.g. "Mezzanine Level (Service Plant Area)")
// can't force the button wider than its dialog parent — which on mobile
// would cause the button to overflow the narrowed (`w-[calc(100%-2rem)]`)
// Dialog card and visually clip at the dialog edge. The label span
// applies `break-words` so the same long names wrap to a second line
// inside the button instead of being truncated.
const LIST_ITEM_BUTTON_BASE =
  'flex w-full min-w-0 min-h-11 items-center justify-between rounded-md border border-gray-300 bg-white px-4 py-3 text-left text-base font-medium text-gray-900 shadow-sm hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)] disabled:cursor-not-allowed disabled:opacity-50'

const LIST_ITEM_LABEL = 'min-w-0 break-words'

// Selected state: thicker brand border + soft brand-tinted fill, no
// ring. The previous treatment used `ring-2` which sits *outside* the
// button's box; when the selected row landed at the top or bottom of
// the `<ul overflow-y-auto>` container, the ring's outer edge was
// clipped by the scroll-mask and the selected button looked like its
// border was truncated. Switching from ring → border-2 keeps the
// emphasis inside the button box so nothing can be clipped, and the
// `/15` brand-tint fill gives an unmistakable backdrop while keeping
// the base text colour readable (the previous attempt used cream text
// on the solid `--color-brand` fill, which Codex flagged as lime-on-
// cream low-contrast).
const LIST_ITEM_BUTTON_SELECTED =
  'border-2 border-[color:var(--color-brand)] bg-[color:var(--color-brand)]/15 hover:bg-[color:var(--color-brand)]/15'

// Shared `<ul>` container for both the floor and room-type pickers.
// The mobile WebKit default scrollbar is wide and visually noisy
// inside this dialog; the arbitrary-variant utilities below render a
// thin brand-neutral track on iOS Safari + Android Chrome, while the
// arbitrary-property `[scrollbar-width:thin]` handles Firefox (incl.
// Firefox for Android). Note: the first-party `scrollbar-thin` Tailwind
// utility was added in Tailwind 4.3 and this project pins 4.2.2 in
// `apps/frontend/package.json` — using the underlying CSS property
// directly keeps the fix working without an upgrade or plugin. The
// affordance stays visible so field staff can still tell the list is
// scrollable.
const LIST_CONTAINER_CLASSES =
  'flex max-h-80 flex-col gap-2 overflow-y-auto [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300'

export function NewRoomDialog({
  open,
  onClose,
  sessionId,
  floors,
  roomTypes,
  floorsLoading = false,
  dispatch,
  onCreated,
}: NewRoomDialogProps): ReactElement {
  const [step, setStep] = useState<Step>('floor')
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null)
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState<string | null>(
    null,
  )
  // Optional surveyor-supplied room name (FFR2). Empty string == not
  // entered; trimmed-blank collapses to undefined at dispatch time so
  // the mutation carries no alias and the row keeps its generated name.
  const [alias, setAlias] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset is driven by explicit gestures rather than a reactive
  // effect: Dialog's Esc / overlay / Cancel all route through
  // `handleClose`, and the success path resets in `handleCreate`
  // before calling `onClose`. Keeping the reset out of useEffect is
  // required by React 19's `react-hooks/set-state-in-effect` rule,
  // which correctly flags the cascading-render hazard of observing
  // an `open` transition in state.
  const resetState = useCallback(() => {
    setStep('floor')
    setSelectedFloorId(null)
    setSelectedRoomTypeId(null)
    setAlias('')
    setSubmitting(false)
    setError(null)
  }, [])

  // Picker excludes pseudo room types via `isSerialisable === true`.
  // Sort mirrors buildspec 20-ui §5.4a (displayOrder, then name).
  const pickableRoomTypes = useMemo(() => {
    return [...roomTypes]
      .filter((rt) => rt.isSerialisable)
      .sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return a.displayOrder - b.displayOrder
        }
        return a.name.localeCompare(b.name)
      })
  }, [roomTypes])

  // Name of the currently-selected room type, used to make the alias
  // helper text concrete ("Defaults to Office 3"). Falls back to a
  // neutral example before a room type is picked.
  const selectedRoomTypeName = useMemo(() => {
    if (selectedRoomTypeId === null) return null
    return (
      pickableRoomTypes.find((rt) => rt.id === selectedRoomTypeId)?.name ?? null
    )
  }, [pickableRoomTypes, selectedRoomTypeId])

  const handleSelectFloor = (id: string): void => {
    setSelectedFloorId(id)
    setStep('roomType')
    setError(null)
  }

  const handleBack = (): void => {
    setStep('floor')
    setSelectedRoomTypeId(null)
    setError(null)
  }

  const handleClose = (): void => {
    if (submitting) return
    resetState()
    onClose()
  }

  const handleCreate = async (): Promise<void> => {
    if (selectedFloorId === null || selectedRoomTypeId === null) return
    const selectedFloor = floors.find((f) => f.id === selectedFloorId)
    if (selectedFloor === undefined) return
    setSubmitting(true)
    setError(null)
    try {
      // The picker shows contract-scoped floor_options. Resolve the
      // chosen name to a per-building floor row id (find-or-create)
      // BEFORE the create-space dispatch — the existing
      // /capture/sessions/:id/spaces endpoint still expects a
      // per-building floor id, and PowerSync local writes mirror that
      // schema. The `resolveFloor` seam routes to the REST endpoint
      // online or the local SQLite mirror in a PowerSync build, so this
      // call works offline too; both honour the same ownership / state
      // guards createSpace applies (see resolve-floor.ts in this dir,
      // apps/backend/src/capture/resolve-floor.ts, and
      // apps/frontend/src/hooks/powersync/resolve-floor-local.ts —
      // Codex P2 on PR #421).
      const { floorId: resolvedFloorId } = await resolveFloor(
        sessionId,
        selectedFloor.name,
      )
      // Collapse a blank / whitespace-only input to undefined so the
      // mutation omits alias entirely (room keeps its generated name).
      // A non-empty value is trimmed here; the backend re-applies the
      // same trim + 255-cap defensively.
      const trimmedAlias = alias.trim()
      const result = await dispatch({
        kind: 'createSpace',
        sessionId,
        floorId: resolvedFloorId,
        roomTypeId: selectedRoomTypeId,
        alias: trimmedAlias.length > 0 ? trimmedAlias : undefined,
      })
      // The transport splits SpaceWithItems into {space, items}; we
      // reassemble so parents get the exact DTO the server returned.
      if (result.kind !== 'createSpace') {
        throw new Error('Unexpected mutation result kind')
      }
      const space = {
        ...(result.space as Omit<SpaceWithItems, 'items'>),
        items: result.items as readonly CaptureItemRow[],
      } as SpaceWithItems
      resetState()
      onCreated(space)
      onClose()
    } catch (err) {
      setSubmitting(false)
      setError(errorMessage(err))
    }
  }

  const title = 'New room'
  const description =
    step === 'floor'
      ? 'Select the floor where this room is located.'
      : 'Select the room type for this new room.'

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={title}
      description={description}
      dismissOnOverlayClick={!submitting}
    >
      <div className="flex flex-col gap-4">
        {error !== null ? (
          <Alert variant="error" title="Could not create room">
            {error}
          </Alert>
        ) : null}

        {step === 'floor' ? (
          <FloorList
            floors={floors}
            isLoading={floorsLoading}
            onSelect={handleSelectFloor}
          />
        ) : (
          <>
            <RoomTypeList
              roomTypes={pickableRoomTypes}
              selectedId={selectedRoomTypeId}
              onSelect={setSelectedRoomTypeId}
              disabled={submitting}
            />
            {/* Optional surveyor-supplied room name (FFR2). Leaving it
                 blank keeps the app-assigned name (e.g. "Office 3"); a
                 value here is shown as a secondary label in the room
                 list and never replaces the canonical name or reaches
                 the CSV export. */}
            <AliasField
              value={alias}
              onChange={setAlias}
              disabled={submitting}
              roomTypeName={selectedRoomTypeName}
            />
          </>
        )}

        <div className="flex justify-end gap-2">
          {step === 'floor' ? (
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={handleBack}
                disabled={submitting}
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  void handleCreate()
                }}
                loading={submitting}
                disabled={selectedRoomTypeId === null || submitting}
              >
                Create room
              </Button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  )
}

function FloorList({
  floors,
  isLoading,
  onSelect,
}: {
  floors: readonly NewRoomDialogFloor[]
  isLoading: boolean
  onSelect: (id: string) => void
}): ReactElement {
  if (isLoading && floors.length === 0) {
    return (
      <div
        className="flex items-center gap-2 text-sm text-gray-600"
        data-testid="floors-loading"
      >
        <Spinner size="sm" label="Loading floors" />
        <span>Loading floors…</span>
      </div>
    )
  }
  if (floors.length === 0) {
    // Floors are admin-curated globally (contract-scoped floor_options,
    // PR #421). Surface that to the surveyor so they don't read this
    // as a per-building config they can fix themselves.
    return (
      <p className="text-sm text-gray-600">
        No floors have been configured. Ask an admin to add floors in
        Templates → Floors.
      </p>
    )
  }
  return (
    <ul
      role="list"
      aria-label="Floors"
      className={LIST_CONTAINER_CLASSES}
    >
      {floors.map((floor) => (
        <li key={floor.id}>
          <button
            type="button"
            className={LIST_ITEM_BUTTON_BASE}
            onClick={() => onSelect(floor.id)}
          >
            <span className={LIST_ITEM_LABEL}>{floor.name}</span>
          </button>
        </li>
      ))}
    </ul>
  )
}

function RoomTypeList({
  roomTypes,
  selectedId,
  onSelect,
  disabled,
}: {
  roomTypes: readonly NewRoomDialogRoomType[]
  selectedId: string | null
  onSelect: (id: string) => void
  disabled: boolean
}): ReactElement {
  if (roomTypes.length === 0) {
    return (
      <p className="text-sm text-gray-600">
        No room types available. Ask an admin to add serialisable room types.
      </p>
    )
  }
  return (
    <ul
      role="list"
      aria-label="Room types"
      className={LIST_CONTAINER_CLASSES}
    >
      {roomTypes.map((rt) => {
        const isSelected = selectedId === rt.id
        const classes = isSelected
          ? `${LIST_ITEM_BUTTON_BASE} ${LIST_ITEM_BUTTON_SELECTED}`
          : LIST_ITEM_BUTTON_BASE
        return (
          <li key={rt.id}>
            <button
              type="button"
              className={classes}
              aria-pressed={isSelected}
              disabled={disabled}
              onClick={() => onSelect(rt.id)}
            >
              <span className={LIST_ITEM_LABEL}>{rt.name}</span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

function AliasField({
  value,
  onChange,
  disabled,
  roomTypeName,
}: {
  value: string
  onChange: (next: string) => void
  disabled: boolean
  roomTypeName: string | null
}): ReactElement {
  // Concrete example in the helper text so field staff understand the
  // field is optional and what the automatic name looks like. Uses the
  // picked room type when known, else a neutral placeholder.
  const example = `${roomTypeName ?? 'Office'} 3`
  return (
    <FormField
      label="Room name (optional)"
      htmlFor="new-room-alias"
      description={`Leave blank to use the automatic name (e.g. ${example}).`}
    >
      <TextInput
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        maxLength={ALIAS_MAX_LENGTH}
        autoComplete="off"
        placeholder={`e.g. ${example}`}
      />
    </FormField>
  )
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    // Defensive: we filter pseudo room types client-side, but the
    // server's 400 "pseudo_room_type" is the exact message we'd want
    // to surface if the catalogue drifts under us.
    return err.message
  }
  if (err instanceof Error) {
    return err.message
  }
  return 'Could not create room. Please try again.'
}
